import { NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LAYOUT, AUTH } from "@/constants/testIds";
import { PasswordInput, PasswordStrength, isStrongPassword, PASSWORD_POLICY_MSG } from "@/components/PasswordInput";
import { SecurityPinModal } from "@/components/SecurityPinModal";
import { toast } from "@/lib/notify";
import {
  LayoutDashboard,
  Users,
  Table2,
  Receipt,
  Network,
  FileBarChart2,
  Heart,
  LogOut,
  ShieldCheck,
  KeyRound,
  Download,
  X,
  AlertCircle,
  CheckCircle2,
  ClipboardList,
  CalendarCheck,
  Coffee,
} from "lucide-react";

// Nav items, each with `roles` listing who can see them.
// "administrador" sees everything; "secretaria" sees Cells, Consolidation,
// plus the new Secretaría-focused modules (Solicitudes, Agenda, Koinonía).
const ROLE_ADMIN = "administrador";
const ROLE_SEC = "secretaria";

const NAV = [
  { to: "/", label: "Panel", icon: LayoutDashboard, testid: LAYOUT.navDashboard, end: true, roles: [ROLE_ADMIN] },
  { to: "/miembros", label: "Miembros", icon: Users, testid: LAYOUT.navMembers, roles: [ROLE_ADMIN] },
  { to: "/captura", label: "Captura Masiva", icon: Table2, testid: LAYOUT.navCapture, roles: [ROLE_ADMIN] },
  { to: "/gastos", label: "Gastos", icon: Receipt, testid: LAYOUT.navExpenses, roles: [ROLE_ADMIN] },
  { to: "/celulas", label: "Células", icon: Network, testid: LAYOUT.navCells, roles: [ROLE_ADMIN, ROLE_SEC] },
  { to: "/consolidacion", label: "Consolidación", icon: Heart, testid: LAYOUT.navConsolidation, roles: [ROLE_ADMIN, ROLE_SEC] },
  { to: "/solicitudes", label: "Solicitudes", icon: ClipboardList, testid: LAYOUT.navRequests, roles: [ROLE_ADMIN, ROLE_SEC] },
  { to: "/agenda", label: "Mi Agenda", icon: CalendarCheck, testid: LAYOUT.navAgenda, roles: [ROLE_ADMIN, ROLE_SEC] },
  { to: "/koinonia", label: "Koinonía Café", icon: Coffee, testid: LAYOUT.navKoinonia, roles: [ROLE_ADMIN, ROLE_SEC] },
  { to: "/reportes", label: "Reportes", icon: FileBarChart2, testid: LAYOUT.navReports, roles: [ROLE_ADMIN] },
  { to: "/usuarios", label: "Usuarios", icon: ShieldCheck, testid: LAYOUT.navUsers, roles: [ROLE_ADMIN] },
];

const ROLE_LABEL = {
  administrador: "Administrador",
  secretaria: "Secretaría",
};

export default function AppLayout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinPromptDismissed, setPinPromptDismissed] = useState(
    () => sessionStorage.getItem("ajar_pin_prompt_dismissed") === "1",
  );

  const dismissPinPrompt = () => {
    sessionStorage.setItem("ajar_pin_prompt_dismissed", "1");
    setPinPromptDismissed(true);
  };

  const [backupReminderDue, setBackupReminderDue] = useState(false);
  const isAdmin = user?.role === "administrador";

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/backups/reminder")
      .then(({ data }) => setBackupReminderDue(!!data.due))
      .catch(() => {});
  }, [isAdmin]);

  const downloadBackupZip = async () => {
    try {
      const res = await api.get("/backup.zip", { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `ajar_backup_${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setBackupReminderDue(false);
      toast.success("Respaldo descargado. Guárdalo en un lugar seguro.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error descargando respaldo");
    }
  };

  const snoozeBackupReminder = async () => {
    setBackupReminderDue(false);
    try {
      await api.post("/backups/reminder/snooze");
    } catch (err) {
      console.warn("No se pudo posponer el recordatorio de respaldo", err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f0e6] text-[#5d5849]">
        Cargando...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const role = user.role || ROLE_ADMIN;
  const visibleNav = NAV.filter((item) => item.roles.includes(role));

  // If secretaria lands on an admin-only path, redirect to /celulas
  const allowedPaths = visibleNav.map((n) => n.to);
  const currentPath = location.pathname;
  const isAllowed = allowedPaths.some((p) =>
    p === "/" ? currentPath === "/" : currentPath.startsWith(p),
  );
  if (!isAllowed) {
    const fallback = role === ROLE_SEC ? "/celulas" : "/";
    return <Navigate to={fallback} replace />;
  }

  return (
    <div className="min-h-screen flex bg-[#f5f0e6] text-[#1c1a14]">
      <aside
        data-testid={LAYOUT.sidebar}
        className="w-64 bg-[#0f1a2c] text-[#f5f0e6] flex flex-col"
      >
        <div className="px-6 py-6 border-b border-[#1c2a3a]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full border border-[#e7c879] flex items-center justify-center text-[#e7c879] font-serif">
              AJ
            </div>
            <div>
              <div className="font-serif text-lg leading-tight">AJAR</div>
              <div className="text-[10px] tracking-[0.25em] text-[#8b7e5e]">MINISTERIOS</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4">
          {visibleNav.map(({ to, label, icon: Icon, testid, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm transition border-l-2 ${
                  isActive
                    ? "border-[#c8a04a] bg-[#1c2a3a] text-[#e7c879]"
                    : "border-transparent text-[#c4b893] hover:bg-[#162236] hover:text-[#f5f0e6]"
                }`
              }
            >
              <Icon size={16} />
              <span className="tracking-wide">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-6 py-4 border-t border-[#1c2a3a]">
          <div data-testid={LAYOUT.userBadge} className="text-xs text-[#c4b893]">
            <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">Sesión</div>
            <div className="truncate text-[#f5f0e6]">{user.email}</div>
            <div
              data-testid={LAYOUT.userRoleBadge}
              className="mt-1 inline-block text-[9px] uppercase tracking-widest text-[#e7c879] border border-[#c8a04a] rounded px-1.5 py-0.5"
            >
              {ROLE_LABEL[role] || role}
            </div>
          </div>
          <button
            data-testid={LAYOUT.myProfileButton}
            onClick={() => setShowPwdModal(true)}
            className="mt-3 w-full flex items-center justify-center gap-2 bg-transparent hover:bg-[#162236] text-[#c4b893] hover:text-[#f5f0e6] py-1.5 text-[10px] uppercase tracking-widest border border-[#2a3d59]"
          >
            <KeyRound size={12} /> Cambiar contraseña
          </button>
          <button
            data-testid={LAYOUT.securityPinButton}
            onClick={() => setShowPinModal(true)}
            className="mt-2 w-full flex items-center justify-center gap-2 bg-transparent hover:bg-[#162236] text-[#c4b893] hover:text-[#f5f0e6] py-1.5 text-[10px] uppercase tracking-widest border border-[#2a3d59]"
          >
            <ShieldCheck size={12} /> PIN de seguridad (2FA)
          </button>
          <button
            data-testid={AUTH.logoutButton}
            onClick={logout}
            className="mt-2 w-full flex items-center justify-center gap-2 bg-[#1c2a3a] hover:bg-[#243549] text-[#e7c879] py-2 text-xs uppercase tracking-widest border border-[#2a3d59]"
          >
            <LogOut size={14} /> Salir
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        {isAdmin && backupReminderDue && (
          <div
            data-testid={LAYOUT.backupReminderBanner}
            className="flex flex-wrap items-center gap-3 border-b border-[#e8cfa8] bg-[#fbf1dc] px-6 py-3"
          >
            <Download size={18} className="shrink-0 text-[#a3701c]" />
            <div className="flex-1 min-w-[240px] text-[13px] text-[#5b5240]">
              <strong className="text-[#1c1a14]">Copia de seguridad mensual:</strong>{" "}
              ha pasado más de un mes desde la última descarga. Guarde un ZIP de
              respaldo en su computadora como copia externa.
            </div>
            <button
              data-testid={LAYOUT.backupReminderDownload}
              onClick={downloadBackupZip}
              className="rounded border border-[#a3701c] bg-[#a3701c] px-3 py-1.5 text-[11px] uppercase tracking-widest text-white hover:bg-[#8a5d14]"
            >
              Descargar ZIP
            </button>
            <button
              data-testid={LAYOUT.backupReminderSnooze}
              onClick={snoozeBackupReminder}
              className="rounded border border-[#d9cfb8] px-3 py-1.5 text-[11px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]"
            >
              Recordar el próximo mes
            </button>
          </div>
        )}
        {!user.has_pin && !pinPromptDismissed && (
          <div
            data-testid={LAYOUT.pinPromptBanner}
            className="flex flex-wrap items-center gap-3 border-b border-[#e0d5b8] bg-[#fdf6e3] px-6 py-3"
          >
            <ShieldCheck size={18} className="shrink-0 text-[#c8a04a]" />
            <div className="flex-1 min-w-[240px] text-[13px] text-[#5b5240]">
              <strong className="text-[#1c1a14]">Proteja su cuenta:</strong> active un
              PIN de seguridad (2FA) para añadir un segundo paso de verificación al
              iniciar sesión.
            </div>
            <button
              data-testid={LAYOUT.pinPromptActivate}
              onClick={() => {
                dismissPinPrompt();
                setShowPinModal(true);
              }}
              className="rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[11px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a]"
            >
              Activar PIN
            </button>
            <button
              data-testid={LAYOUT.pinPromptDismiss}
              onClick={dismissPinPrompt}
              className="rounded border border-[#d9cfb8] px-3 py-1.5 text-[11px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]"
            >
              Ahora no
            </button>
          </div>
        )}
        <Outlet />
      </main>

      {showPwdModal && (
        <ChangePasswordModal onClose={() => setShowPwdModal(false)} />
      )}
      {showPinModal && (
        <SecurityPinModal onClose={() => setShowPinModal(false)} />
      )}
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isStrongPassword(next)) {
      setError(PASSWORD_POLICY_MSG);
      return;
    }
    if (next !== confirm) {
      setError("Las contraseñas no coinciden");
      return;
    }
    setBusy(true);
    try {
      await api.put("/auth/password", {
        current_password: current,
        new_password: next,
      });
      setSuccess(true);
      setTimeout(onClose, 1400);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al cambiar contraseña");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        data-testid={LAYOUT.changePasswordModal}
        className="w-full max-w-md rounded border border-[#d9cfb8] bg-white p-6 shadow-xl"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg text-[#1c1a14]">Cambiar mi contraseña</h3>
          <button onClick={onClose} className="text-[#8b7e5e] hover:text-[#1c1a14]">
            <X size={18} />
          </button>
        </div>

        {success ? (
          <div
            data-testid={LAYOUT.changePasswordSuccess}
            className="flex items-center gap-2 rounded border border-emerald-300 bg-emerald-50 px-3 py-3 text-[13px] text-emerald-700"
          >
            <CheckCircle2 size={16} /> Contraseña actualizada correctamente.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
                Contraseña actual *
              </span>
              <PasswordInput
                testid={LAYOUT.currentPasswordInput}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded focus:border-[#c8a04a] focus:outline-none"
                autoFocus
                required
              />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
                Nueva contraseña * (mín. 8 · 1 mayúscula · 1 número)
              </span>
              <PasswordInput
                testid={LAYOUT.newPasswordInput}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded focus:border-[#c8a04a] focus:outline-none"
                required
              />
              <PasswordStrength password={next} />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
                Confirmar nueva contraseña *
              </span>
              <PasswordInput
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded focus:border-[#c8a04a] focus:outline-none"
                required
              />
            </label>

            {error && (
              <div
                data-testid={LAYOUT.changePasswordError}
                className="flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700"
              >
                <AlertCircle size={14} /> {error}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                data-testid={LAYOUT.changePasswordCancel}
                onClick={onClose}
                disabled={busy}
                className="rounded border border-[#d9cfb8] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]"
              >
                Cancelar
              </button>
              <button
                type="submit"
                data-testid={LAYOUT.changePasswordSubmit}
                disabled={busy}
                className="rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a] disabled:opacity-50"
              >
                {busy ? "Guardando..." : "Actualizar"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
